import ListFilters from '@/common/components/ListFilters';
import Table from '@/common/components/Table';
import TextField from '@/common/components/TextField';
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker';
import useDebounce from '@/common/hooks/useDebounce';
import { useFilterDate } from '@/common/hooks/useFilterDate';
import { useFilterParams } from '@/common/hooks/useFilterParams';
import { Mapper } from '@/common/services/mapper';
import { UserData } from '@/common/types/users';
import { useUsers } from '@/features/users/hooks/useUsers';
import { SearchIcon } from 'lucide-react';
import { recipientMessagesManagementDefaultColumns } from '../../constants/recipientMessagesManagementDefaultColumns.constant';
import { useRecipientMessages } from '@/common/hooks/queries/useRecipientMessages';
import { RecipientMessagesData, RecipientMessageTableItem } from '../../types/recipientMessages.type';
import { RecipientMessagesManagementTransformer } from '../../services/recipientMessagesManagementTransformer';
import { providerKlassOptions } from '../../constants/providerKlass';
import { recipientMessagesStatusOptions } from '../../constants/recipientMessagesStatus';

export default function RecipientMessagesManagementList() {
  const { filterParams, setFilterParams } = useFilterParams();

  const { date, formattedDate } = useFilterDate(filterParams);

  const [search, setSearch] = useDebounce(filterParams.search || '');
  const [searchUser, setSearchUser] = useDebounce('');

  const { data: recipientMessages, meta: metaRecipientMessages, isLoading: recipientMessagesIsLoading } = useRecipientMessages({
    filters: {
      page: filterParams.page || 1,
      search,
      recipientId: filterParams.user?.value,
      providerKlass: filterParams.providerKlass?.value,
      sentAtGt: formattedDate.from,
      sentAtLt: formattedDate.to,
      status: filterParams.status?.value
    },
    mapper: Mapper.mapWithTransformer<RecipientMessagesData[], RecipientMessageTableItem[]>({ transformer: RecipientMessagesManagementTransformer.tableTransformer() })
  })

  const { data: users } = useUsers({
    filters: { search: searchUser },
    mapper: Mapper.mapToOptions<UserData>({ labelFieldName: 'full_name', valueFieldName: 'id' })
  });

  return (
    <div className="h-[calc((100vh-100px-4rem-1.5rem))] flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <TextField
          name="search"
          LeftIcon={<SearchIcon />}
          placeholder="Pesquisar"
          className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
          onChange={(e) => {
            setSearch((e.target as HTMLInputElement).value);
            setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
          }}
          value={filterParams.search}
        />
        <div className="flex items-center gap-2">
          <DatePickerWithRange date={date} setDate={(date) => setFilterParams((prev) => ({ ...prev, date }))} />

          <ListFilters
            filters={[
              {
                className: '',
                onInputChange: (inputValue) => setSearchUser(inputValue),
                onChange: (e) => setFilterParams((prev) => ({ ...prev, user: e })),
                value: filterParams.user,
                options: [{ label: 'Todos', value: '' }, ...users || []],
                placeholder: 'Usuário'
              },
              {
                className: '',
                onChange: (e) => setFilterParams((prev) => ({ ...prev, providerKlass: e })),
                value: filterParams.providerKlass,
                options: [{ label: 'Todos', value: '' }, ...providerKlassOptions || []],
                placeholder: 'Método',
                dataTestid: 'method'
              },
              {
                className: '',
                onChange: (e) => setFilterParams((prev) => ({ ...prev, status: e })),
                value: filterParams.status,
                options: [{ label: 'Todos', value: '' }, ...recipientMessagesStatusOptions || []],
                placeholder: 'Status',
                dataTestid: 'status'
              },
            ]}
          />
        </div>
      </div>

      <Table
        columns={recipientMessagesManagementDefaultColumns}
        isLoadingData={recipientMessagesIsLoading}
        page={filterParams.page || 1}
        rows={recipientMessages || []}
        totalPages={metaRecipientMessages.total_pages}
        totalCount={metaRecipientMessages.total}
        hasPagination
        setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
      />
    </div>
  )
}
